import {
  Component,
  computed,
  inject,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import { UserModel } from '@models/user/user.model';
import { TerminalUserModel } from '@models/terminal-user/terminal-user.model';
import { UserService } from '@services/user.service';
import { TerminalService } from '@services/terminal.service';
import { TerminalUserService } from '@services/terminal-user.service';
import { ConnectionStatusService } from '@services/connection-status.service';
import { CapitalizePipe } from '@pipes/capitalize.pipe';
import { LoaderComponent } from '@components/app-loader/app-loader.component';

type TerminalUserPermissionKey =
  | 'can_open_session'
  | 'can_close_session'
  | 'can_force_order'
  | 'is_active';

const ASSIGNABLE_ROLES = ['ROLE_SELF_AGENT', 'ROLE_SELF_MANAGER', 'ROLE_SELF_ADMIN'];

interface TerminalUserRow {
  user: UserModel;
  permission: TerminalUserModel | null;
}

@Component({
  selector: 'app-terminal-user-permissions',
  standalone: true,
  imports: [TranslateModule, CapitalizePipe, LoaderComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './terminal-user-permissions.component.html',
})
export class TerminalUserPermissionsComponent implements OnInit {
  private readonly terminalService = inject(TerminalService);
  private readonly terminalUserService = inject(TerminalUserService);
  private readonly userService = inject(UserService);
  private readonly connectionStatusService = inject(ConnectionStatusService);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  protected readonly isOffline = computed(
    () => this.connectionStatusService.status() === 'common.offline',
  );

  protected readonly permissionColumns: TerminalUserPermissionKey[] = [
    'can_open_session',
    'can_close_session',
    'can_force_order',
    'is_active',
  ];

  protected readonly isLoading = signal(false);
  protected readonly loadError = signal(false);
  protected readonly savingUserId = signal<number | null>(null);

  #users = signal<UserModel[]>([]);
  #terminalUsers = signal<TerminalUserModel[]>([]);

  protected readonly rows = computed<TerminalUserRow[]>(() => {
    const terminalUsers = this.#terminalUsers();
    return this.#users().map((user) => ({
      user,
      permission: terminalUsers.find((tu) => tu.user_id === this.#getRemoteUserId(user)) ?? null,
    }));
  });

  ngOnInit(): void {
    this.#loadData();
  }

  #loadData(): void {
    const terminalId = this.terminalService.currentTerminal()?.id;
    if (!terminalId) {
      return;
    }

    this.isLoading.set(true);
    this.loadError.set(false);

    forkJoin({
      users: this.userService.getUsers(),
      terminalUsers: this.terminalUserService.getTerminalUsers(terminalId),
    }).subscribe({
      next: ({ users, terminalUsers }) => {
        this.#users.set(
          users.filter((user) => user.roles.some((role) => ASSIGNABLE_ROLES.includes(role))),
        );
        this.#terminalUsers.set(terminalUsers);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.loadError.set(true);
      },
    });
  }

  protected togglePermission(row: TerminalUserRow, key: TerminalUserPermissionKey): void {
    const terminalId = this.terminalService.currentTerminal()?.id;
    if (!terminalId || this.savingUserId() !== null) {
      return;
    }

    if (this.isOffline()) {
      this.toastr.error(
        this.translate.instant('admin_settings.terminal_permissions.errors.offline_message'),
        this.translate.instant('admin_settings.terminal_permissions.errors.offline_title'),
      );
      return;
    }

    const remoteUserId = this.#getRemoteUserId(row.user);

    const current = row.permission;
    const nextValues = {
      self_pos_terminal_id: terminalId,
      user_id: remoteUserId,
      can_open_session: current?.can_open_session ?? false,
      can_close_session: current?.can_close_session ?? false,
      can_force_order: current?.can_force_order ?? false,
      is_active: current?.is_active ?? false,
      [key]: !(current?.[key] ?? false),
    };

    const previousTerminalUsers = this.#terminalUsers();
    const optimisticPermission: TerminalUserModel = { id: current?.id ?? -1, ...nextValues };

    this.savingUserId.set(row.user.id);
    this.#terminalUsers.update((list) => [
      ...list.filter((tu) => tu.user_id !== remoteUserId),
      optimisticPermission,
    ]);

    const request$ = current
      ? this.terminalUserService.updateTerminalUser({ id: current.id, ...nextValues })
      : this.terminalUserService.createTerminalUser(nextValues);

    request$.subscribe({
      next: () => this.savingUserId.set(null),
      error: () => {
        this.savingUserId.set(null);
        this.#terminalUsers.set(previousTerminalUsers);
        this.toastr.error(
          this.translate.instant('admin_settings.terminal_permissions.errors.update_message'),
          this.translate.instant('admin_settings.terminal_permissions.errors.update_title'),
        );
      },
    });
  }

  protected getUserDisplayName(user: UserModel): string {
    const fullName = `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim();
    return fullName || user.username;
  }

  #getRemoteUserId(user: UserModel): number {
    return user.remote_id ?? user.id;
  }
}
